import React, { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { graphql } from 'react-relay';

import { expVal } from '@confluence/feature-experiments';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { isRovoSystemSpaceKey } from '@confluence/route-manager/entry-points/isRovoSystemSpaceKey.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	SpaceOverviewSwitcherQuery$data,
	SpaceOverviewSwitcherQuery$variables,
} from './__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { SpaceOverview } from './SpaceOverview.tsx';
import { SmartSpaceOverview } from './smart-space-overview/SmartSpaceOverview.tsx';

export const SpaceOverviewSwitcherQuery = graphql`
	query SpaceOverviewSwitcherQuery($cloudId: ID!, $spaceKey: String!) {
		confluence_smartSpaceOverview(cloudId: $cloudId, spaceKey: $spaceKey) {
			summary {
				body
			}
			faq {
				entries {
					question
					answer
					followUpQuestions
					sources {
						content {
							id
							title
							excerpt
							embeddedProduct
							links {
								webui
							}
						}
					}
				}
			}
			topLinks {
				contentId
				lastRefreshed
				links {
					isPinned
					title
					url
				}
			}
		}
	}
`;

type SpaceOverviewExperience = 'smart' | 'regular';

type SpaceOverviewSwitcherProps = {
	spaceKey: string;
	children?: ReactNode;
	onExperienceResolved?: (experience: SpaceOverviewExperience) => void;
};

export const SpaceOverviewSwitcher = ({
	spaceKey,
	children,
	onExperienceResolved,
}: SpaceOverviewSwitcherProps) => {
	const { isRovoEnabled, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const spaceId = useSpaceId(spaceKey);
	const trackedResolvedExperienceRef = useRef<SpaceOverviewExperience | null>(null);

	const isSmartSpaceOverviewEnabled =
		!isCompanyHubSpaceKey(spaceKey) &&
		!isRovoSystemSpaceKey(spaceKey) &&
		isRovoEnabled &&
		expVal('smart_space_overview', 'isEnabled', false);

	const { data, loading } = useQueryInterop<
		SpaceOverviewSwitcherQuery$data,
		SpaceOverviewSwitcherQuery$variables
	>(SpaceOverviewSwitcherQuery, {
		variables: {
			cloudId,
			spaceKey,
		},
		skip: !isSmartSpaceOverviewEnabled || !cloudId,
	});

	const smartSpaceOverviewData = data?.confluence_smartSpaceOverview;
	const hasSummary = !!smartSpaceOverviewData?.summary?.body;
	const resolvedExperience: SpaceOverviewExperience | null = isSmartSpaceOverviewEnabled
		? loading
			? null
			: hasSummary
				? 'smart'
				: 'regular'
		: 'regular';

	useEffect(() => {
		if (resolvedExperience) {
			onExperienceResolved?.(resolvedExperience);

			if (spaceId && trackedResolvedExperienceRef.current !== resolvedExperience) {
				trackedResolvedExperienceRef.current = resolvedExperience;
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'resolved',
						actionSubject: 'spaceOverviewExperience',
						source: 'spaceOverviewScreen',
						containerId: spaceId,
						attributes: {
							spaceOverviewExperience: resolvedExperience,
							spaceKey,
						},
					},
				}).fire();
			}
		}
	}, [createAnalyticsEvent, resolvedExperience, onExperienceResolved, spaceId, spaceKey]);

	if (isSmartSpaceOverviewEnabled) {
		if (loading) {
			return null;
		}

		if (hasSummary) {
			return (
				<SmartSpaceOverview
					spaceKey={spaceKey}
					smartSpaceOverviewData={smartSpaceOverviewData}
					cloudId={cloudId}
				/>
			);
		}
	}

	return <SpaceOverview spaceKey={spaceKey}>{children}</SpaceOverview>;
};
